import * as React from "react";
import { useDispatch } from "react-redux";

import { AccordionQuestionnaireContainer } from "../../questionnaires/components/AccordionQuestionnaireContainer";
import {
    MANAGER_SATISFACTION_FOLLOW_UP_QUESTIONNAIRE,
    QuestionnaireFormProps,
    whenDidTheyReturnToWorkOptions,
    whichOfTheFollowingBestDescribesTheCurrentWorkSituationOptions,
    whichOfTheFollowingBestDescribesTheSituationWithRegardsToReturnToWorkInTheFutureOptions,
    whichOfTheFollowingBestDescribesWhyTheEmployeeHasNotReturnedToWorkOptions
} from "../models";
import { AccordionQuestionnaireSection } from "../../questionnaires/components/AccordionQuestionnaireSection";
import { RadioButton, RadioButtonQuestion, YesOrNo } from "../../questionnaires/components/questions/RadioButtonQuestion";
import { QuestionConfig } from "../../questionnaires/questionnaireConfig";
import { QuestionAnswer, QuestionnaireTypes } from "../../questionnaires/model";
import { questionnaireBMasterConfig as masterConfig } from "../questionnaireBMasterConfig";
import * as questionIds from "../questionnaireBQuestionsIds";
import { DropdownQuestion } from "../../questionnaires/components/questions/DropdownQuestion";
import { clearQuestionnaire } from "../../questionnaires/actions";

export const QuestionnaireB: React.FunctionComponent<QuestionnaireFormProps> = (props: QuestionnaireFormProps) => {
    const dispatch = useDispatch();

    const isReadonly = props.task.completed === true;

    React.useEffect(() => {
        return () => {
            dispatch(clearQuestionnaire(MANAGER_SATISFACTION_FOLLOW_UP_QUESTIONNAIRE));
        };
    }, []);

    const loadQuestionnaire = () => {
        if (props.loadQuestionnaire) {
            props.loadQuestionnaire(props.ohCase.customer?.id, props.ohCase.id, props.task.id);
        }
    };

    const getQuestionConfig = (questionId: string): QuestionConfig => {
        return masterConfig.find(c => c.id === questionId) || { id: questionId, disabled: true };
    };

    const getAnswerToQuestionWithId = (id: string): QuestionAnswer => {
        return props.questions?.find(question => question.id === id)?.answer;
    };

    const isTheIndividualMentionedAboveCurrentlyAtWorkAndNoLongerAbsentDueToSicknessAnswer =
        getAnswerToQuestionWithId(questionIds.IsTheIndividualMentionedAboveCurrentlyAtWorkAndNoLongerAbsentDueToSickness);

    return (
        <AccordionQuestionnaireContainer
            loadQuestionnaire={loadQuestionnaire}
            submitQuestionnaire={() => props.submitQuestionnaire(props.questions)}
            readonly={isReadonly}
            questionnaireKey={MANAGER_SATISFACTION_FOLLOW_UP_QUESTIONNAIRE}
            questionnaireType={QuestionnaireTypes.Other}
        >
            <AccordionQuestionnaireSection
                title={"Satisfaction Questionnaire"}
                id={"SatisfactionQuestionnaire"}
                hideNextButton={true}
                active={true}
            >
                <RadioButtonQuestion
                    questionConfig={getQuestionConfig(questionIds.IsTheIndividualMentionedAboveCurrentlyAtWorkAndNoLongerAbsentDueToSickness)}
                    readonly={isReadonly}
                    mandatory={true}
                >
                    <RadioButton label="Yes" value={YesOrNo.Yes} />
                    <RadioButton label="No" value={YesOrNo.No} />
                    <RadioButton label="Don't know" value={YesOrNo.DoNotKnow} />
                </RadioButtonQuestion>
                {isTheIndividualMentionedAboveCurrentlyAtWorkAndNoLongerAbsentDueToSicknessAnswer === YesOrNo.Yes &&
                    <>
                        <DropdownQuestion
                            questionConfig={getQuestionConfig(questionIds.WhenDidTheyReturnToWork)}
                            readonly={isReadonly}
                            mandatory={true}
                            options={whenDidTheyReturnToWorkOptions.sort(o => o.order)}
                            hideDivider
                        />
                        <DropdownQuestion
                            questionConfig={getQuestionConfig(questionIds.WhichOfTheFollowingBestDescribesTheCurrentWorkSituation)}
                            readonly={isReadonly}
                            mandatory={true}
                            options={whichOfTheFollowingBestDescribesTheCurrentWorkSituationOptions.sort(o => o.order)}
                            hideDivider
                        />
                        <RadioButtonQuestion
                            questionConfig={getQuestionConfig(questionIds.AtWorkDidTheReportAndRecommendationsHaveHelpedToFacilitateSustainableReturnToWork)}
                            readonly={isReadonly}
                            mandatory={true}
                        >
                            <RadioButton label="Yes" value={YesOrNo.Yes} />
                            <RadioButton label="No" value={YesOrNo.No} />
                            <RadioButton label="Not sure" value={YesOrNo.NotSure} />
                        </RadioButtonQuestion>
                    </>
                }
                {isTheIndividualMentionedAboveCurrentlyAtWorkAndNoLongerAbsentDueToSicknessAnswer === YesOrNo.No &&
                    <>
                        <DropdownQuestion
                            questionConfig={getQuestionConfig(questionIds.WhichOfTheFollowingBestDescribesTheSituationWithRegardsToReturnToWorkInTheFuture)}
                            readonly={isReadonly}
                            mandatory={true}
                            options={whichOfTheFollowingBestDescribesTheSituationWithRegardsToReturnToWorkInTheFutureOptions.sort(o => o.order)}
                            hideDivider
                        />
                        <DropdownQuestion
                            questionConfig={getQuestionConfig(questionIds.WhichOfTheFollowingBestDescribesWhyTheEmployeeHasNotReturnedToWork)}
                            readonly={isReadonly}
                            mandatory={true}
                            options={whichOfTheFollowingBestDescribesWhyTheEmployeeHasNotReturnedToWorkOptions.sort(o => o.order)}
                            hideDivider
                        />
                        <RadioButtonQuestion
                            // eslint-disable-next-line max-len
                            questionConfig={getQuestionConfig(questionIds.NotAtWorkDidTheReportAndRecommendationsHelpWithManagementOfTheIndividualsAbsenceFromWork)}
                            readonly={isReadonly}
                            mandatory={true}
                        >
                            <RadioButton label="Yes" value={YesOrNo.Yes} />
                            <RadioButton label="No" value={YesOrNo.No} />
                            <RadioButton label="Not sure" value={YesOrNo.NotSure} />
                        </RadioButtonQuestion>
                    </>
                }
                {isTheIndividualMentionedAboveCurrentlyAtWorkAndNoLongerAbsentDueToSicknessAnswer === YesOrNo.DoNotKnow &&
                    <RadioButtonQuestion
                        // eslint-disable-next-line max-len
                        questionConfig={getQuestionConfig(questionIds.DontKnowDidTheReportAndRecommendationsHelpWithManagementOfTheIndividualsAbsenceFromWork)}
                        readonly={isReadonly}
                        mandatory={true}
                    >
                        <RadioButton label="Yes" value={YesOrNo.Yes} />
                        <RadioButton label="No" value={YesOrNo.No} />
                        <RadioButton label="Not sure" value={YesOrNo.NotSure} />
                    </RadioButtonQuestion>
                }
            </AccordionQuestionnaireSection>
        </AccordionQuestionnaireContainer>
    );
};
